import classNames from "classnames";
import React, { type AnchorHTMLAttributes, useMemo } from "react";
import { type LinkProps, Link as ReactLink, useLocation } from "react-router-dom";

import { useLayoutStore } from "@store/layout";

interface IProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  as?: "text" | "wrapper" | "square";
  variant?: "primary" | "secondary";
  to?: LinkProps["to"];
  state?: LinkProps["state"];
  isActive?: boolean;
}

const extractText = (children: React.ReactNode): string => {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(extractText).join("");
  }
  if (React.isValidElement(children)) {
    return extractText(children.props.children);
  }
  return "";
};

export default function Link({ className, children, as = "wrapper", variant = "primary", href = "", isActive = true, ...props }: IProps) {
  const { pathname } = useLocation();

  const { closeAll } = useLayoutStore();

  const isExternalLink = href.includes("https://");

  const isKorean = useMemo(() => {
    const text = extractText(children);
    return /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(text);
  }, [children]);

  const linkStyle = classNames(
    "inline-block cursor-pointer hover:text-black dark:hover:text-white",
    isKorean && "text-[14px]!",
    !isActive && "text-dark-grey hover:text-black dark:hover:text-white",
    as === "square" && [
      "text-subtitle text-base! items-center pc:h-8.75 h-10 px-3.5 py-1.5 w-fit",
      variant === "primary" && "bg-black text-white disabled:bg-grey dark:bg-white dark:text-black hover:text-white",
      variant === "secondary" && "border border-solid flex! border-grey  disabled:border-grey dark:border-dark-grey",
    ],
    className,
  );

  return (
    <>
      {isExternalLink ? (
        <a className={linkStyle} href={href} rel="noopener" target="_blank" {...props}>
          {children}
        </a>
      ) : (
        <ReactLink
          className={linkStyle}
          to={href}
          {...props}
          onClick={(e) => {
            if (pathname.replaceAll("/", "") === href.replaceAll("/", "")) {
              window.scrollTo(0, 0);
              closeAll();
            }
            props?.onClick?.(e);
          }}
        >
          {children}
        </ReactLink>
      )}
    </>
  );
}
